import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import globalizationService from '../../services/globalizationService';
import filterService from '../../services/filterService';
import { storeUserLanguage } from '../../redux/actions/globalizationAction';
import EpToaster from '../../components/EpToaster';
import EpBrowsableFilter from '../../components/EpBrowsableFilter';
import EpFilter from '../../components/EpFilter';
import EpGlobalNavigation from '../../components/EpGlobalNavigation';
import EpGlobalFooter from '../../components/EpGlobalFooter';
import EpCardIndicator from '../../components/EpCardIndicator';
import EpTable from '../../components/EpTable';
import EpLoadingPage from '../../components/EpLoadingPage';
import { storeLastFiltersApplied } from '../../redux/actions/filterAction';
import indicatorsService from '../../services/indicatorsService';
import EpRecordInformationsModal from '../../components/EpRecordInformationsModal';
import useTranslation from '../../hooks/useTranslation';
import {
  emptyNumberCell,
  emptyStringCell,
  uniquePercentValueCell,
  doubleIndicatorsCell,
  coloredConsolidatedIndicatorCell,
  recordsHoverableCell,
  tendencyChartCell,
  coloredEvolutiveIndicatorCell,
} from '../../utils/table';
import './styles.scss';
import { changeSelectValue } from '../../components/EpFormSelect';

const Benchmarking = () => {
  // Tags do Globalization
  const Year = useTranslation('year');
  const Years = useTranslation('years');
  const Indicator = useTranslation('Indicator');
  const Admissions = useTranslation('Admissions');
  const Age = useTranslation('Age');
  const FortyEightreadmissions = useTranslation('Readmission <48h');
  const SOFA = useTranslation('SOFA');
  const RenalReplacementTherapy = useTranslation('Renal Replacement Therapy');
  // const MechanicalVentilationPatients = useTranslation('Mechanical Ventilation (patients)');
  // const MechanicalVentilationMean = useTranslation('Mechanical Ventilation (mean)');
  const LOSICU = useTranslation('LOS (ICU)');
  const LOSHospital = useTranslation('LOS (hospital)');
  const MortalityICU = useTranslation('Mortality (ICU)');
  const MortalityHospitalar = useTranslation('Mortality (hospitalar)');
  const MechanicalVentilation = useTranslation('Mechanical Ventilation');
  const Patients = useTranslation('Patients');
  const Mean = useTranslation('Mean');
  const LOS = useTranslation('LOS');
  const ICU = useTranslation('ICU');
  const Hospital = useTranslation('Hospital');
  const Mortality = useTranslation('Mortality');
  const Points = useTranslation('Points');
  const Days = useTranslation('Days');
  const Registry = useTranslation('Registry');
  const Country = useTranslation('Country');
  const MechanicalVentilationDurationDays = useTranslation('Mechanical Ventilation Duration (days)');
  const MechanicalVentilationDuration = useTranslation('Mechanical ventilation duration');
  const ICULOSDays = useTranslation('ICU LOS (days)');
  const HospitalLOSDays = useTranslation('Hospital LOS (days)');
  const ICUMortality = useTranslation('ICU Mortality');
  const HospitalMortality = useTranslation('Hospital Mortality');
  const Trending = useTranslation('Trending');
  const To = useTranslation('To');
  const All = useTranslation('All');
  const English = useTranslation('English');
  const Portuguese = useTranslation('Portuguese');
  const Median = useTranslation('Median');

  const dispatch = useDispatch();
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const currentUserLanguage = localStorage.getItem('bi.userLanguage');

  // Estados do selec de idioma
  const languageSelectRef = useRef();

  const languageOptions = [
    {
      value: 'en-US',
      label: English,
    },
    {
      value: 'pt-BR',
      label: Portuguese,
    },
  ];

  // Estados dos filtros
  const lastFiltersApplied = useSelector(state => state.filter.lastFiltersApplied);
  const filterCache = useSelector(state => state.filter.filterCache);
  const translationsCache = useSelector(state => state.globalization.translationsCache);
  const lastCacheTime = useSelector(state => state.globalization.lastCacheTime);

  // Estados do filtro global
  const filterReportTypeSelected = lastFiltersApplied.reportType;
  const filterIndicatorSelected = lastFiltersApplied.indicator;

  const filterPatientsGroupsOptions = filterCache.patientsGroups
    ? filterCache.patientsGroups[currentUserLanguage] : null;
  const filterPatientsGroupsDefaultOption = filterPatientsGroupsOptions
    ? filterPatientsGroupsOptions.filter(option => option.label === All || option.value === 0)
    : null;
  const filterPatientsGroupsDefaultOptionObj = filterPatientsGroupsDefaultOption
    ? filterPatientsGroupsDefaultOption[0] : { label: All, value: 0 };

  // Estados do filtro local - Anos - Dados Consolidados
  const filterYearsOptions = filterCache.years ? filterCache.years[currentUserLanguage] : null;
  const [filterYearsDefaultOptionObj, setFilterYearsDefaultOptionObj] = useState(filterYearsOptions
    ? filterYearsOptions[0] : null);

  // Estados do filtro local
  const patientsGroupsSelectRef = useRef();
  const browsableFilterSelectRef = useRef();

  // Estados do filtro local - Indicadores - Dados Evolutivos
  const filterIndicatorsOptions = [
    {
      label: Admissions,
      value: 1,
    },
    {
      label: Age,
      value: 2,
    },
    {
      label: FortyEightreadmissions,
      value: 3,
    },
    {
      label: SOFA,
      value: 4,
    },
    {
      label: RenalReplacementTherapy,
      value: 5,
    },
    {
      label: MechanicalVentilation,
      value: 6,
    },
    {
      label: MechanicalVentilationDuration,
      value: 7,
    },
    {
      label: LOSICU,
      value: 8,
    },
    {
      label: LOSHospital,
      value: 9,
    },
    {
      label: MortalityICU,
      value: 10,
    },
    {
      label: MortalityHospitalar,
      value: 11,
    },
  ];
  const filterIndicatorsDefaultOption = filterIndicatorsOptions
    ? filterIndicatorsOptions.filter(option => option.label === Admissions || option.value === 0)
    : null;
  const filterIndicatorsDefaultOptionObj = filterIndicatorsDefaultOption
    ? filterIndicatorsDefaultOption[0] : null;

  // Estados do modal de registros para
  const [modalRecordInformationsTitle, setRecordInformationsTitle] = useState('');
  const [modalRecordInformationsLogo, setModalRecordInformationsLogo] = useState('');
  const [modalRecordInformationsCountryName, setModalRecordInformationsCountryName] = useState('');
  const [modalRecordInformationsHospitalsCount, setModalRecordInformationsHospitalsCount] = useState('');
  const [modalRecordInformationsUnitsCount, setModalRecordInformationsUnitsCount] = useState('');
  const [modalRecordInformationsSourceSite, setModalRecordInformationsSourceSite] = useState('');
  const [modalRecordInformationsDescription, setModalRecordInformationsDescription] = useState('');

  // Estados dos indicatores
  const [evolutiveIndicatorsData, setEvolutiveIndicatorsData] = useState([]);
  const [evolutiveIndicatorsTableColumns, setEvolutiveIndicatorsTableColumns] = useState([]);
  const [evolutiveTableYearColumns, setEvolutiveTableYearColumns] = useState([]);
  const [evolutiveBeginYear, setEvolutiveBeginYear] = useState('');
  const [evolutiveEndYear, setEvolutiveEndYear] = useState('');

  const [consolidatedIndicatorsData, setConsolidatedIndicatorsData] = useState([]);
  const [consolidatedIndicatorsTableColumns, setConsolidatedIndicatorsTableColumns] = useState([]);
  const [consolidatedTotalizerIndicatorsData, setConsolidatedTotalizerIndicatorsData] = useState(
    null,
  );

  // Atualiza os filtros aplicados
  const updateFiltersAppliedStorage = async () => {
    const newFiltersAppliedObj = {
      ...lastFiltersApplied,
      patientGroupItemCode: filterPatientsGroupsDefaultOptionObj
        ? filterPatientsGroupsDefaultOptionObj.value : null,
      year: filterYearsDefaultOptionObj ? filterYearsDefaultOptionObj.value : null,
      indicator: filterIndicatorsDefaultOptionObj ? filterIndicatorsDefaultOptionObj.value : null,
    };

    let queryStrings = {
      selectedPatientGroup: newFiltersAppliedObj.patientGroupItemCode,
      selectedIndicator: newFiltersAppliedObj.indicator,
    };

    if (newFiltersAppliedObj.reportType === 0) {
      queryStrings = {
        selectedYear: newFiltersAppliedObj.year,
        selectedPatientGroup: newFiltersAppliedObj.patientGroupItemCode,
      };

      const consolidatedIndicatorsTotalizersResponseJSON = await indicatorsService()
        .getConsolidatedTotalizers(queryStrings);

      const consolidatedIndicatorsTotalizersList = consolidatedIndicatorsTotalizersResponseJSON
        && consolidatedIndicatorsTotalizersResponseJSON.data
        ? consolidatedIndicatorsTotalizersResponseJSON.data.response : null;

      const consolidatedIndicatorsResponseJSON = await indicatorsService()
        .getConsolidatedIndicators(queryStrings);

      const consolidatedIndicatorsList = consolidatedIndicatorsResponseJSON
        && consolidatedIndicatorsResponseJSON.data
        ? consolidatedIndicatorsResponseJSON.data.response : null;

      setConsolidatedTotalizerIndicatorsData(consolidatedIndicatorsTotalizersList || null);
      setConsolidatedIndicatorsData(consolidatedIndicatorsList || null);
    } else {
      const evolutiveIndicatorsResponseJSON = await indicatorsService()
        .getEvolutiveIndicators(queryStrings);

      const evolutiveIndicatorsList = evolutiveIndicatorsResponseJSON
        && evolutiveIndicatorsResponseJSON.data
        ? evolutiveIndicatorsResponseJSON.data.response : null;

      const updatedIndicatorsData = evolutiveIndicatorsList;

      setEvolutiveIndicatorsData(updatedIndicatorsData || null);
    }

    dispatch(storeLastFiltersApplied(newFiltersAppliedObj));
    setIsLoadingPage(false);
  };

  // Método para alterar o idioma do sistema
  const handleChangeLanguage = (selectedLanguage) => {
    const selectedLanguageCode = selectedLanguage.value;

    if (selectedLanguageCode !== currentUserLanguage) {
      setIsLoadingPage(true);

      axios.defaults.headers.common['Accept-Language'] = selectedLanguageCode || 'en-US';

      // Faz requisição para trazer dados do idioma selecionado
      globalizationService().getDataTermAndTranslationsByTag(
        selectedLanguageCode, translationsCache, lastCacheTime,
      );

      // Faz requisição para trazer dados dos selects do filtro no idioma correto
      filterService().getInitialBenchmarkingFiltersData(
        selectedLanguageCode, filterCache, lastCacheTime,
      );

      setTimeout(() => {
        // Salva e persiste idioma selecionado para cada vez que o usuário logar ter um default
        localStorage.setItem('bi.userLanguage', selectedLanguageCode);
        dispatch(storeUserLanguage(selectedLanguageCode));

        setIsLoadingPage(false);
      }, 300);
    }
  };

  const handleDefaultLanguageOption = () => {
    const defaultSelectOption = languageOptions.filter(
      option => option.value === currentUserLanguage,
    );
    return defaultSelectOption[0];
  };

  const languageDefaultOption = handleDefaultLanguageOption();

  useEffect(() => {
    if (filterPatientsGroupsDefaultOptionObj !== null && filterYearsDefaultOptionObj !== null
      && filterIndicatorsDefaultOptionObj !== null) {
      updateFiltersAppliedStorage();
    }
  }, []);

  useEffect(() => {
    // Encontra ano inicial e final dos dados evolutivos
    const firstYear = filterYearsOptions
      ? filterYearsOptions[filterYearsOptions.length - 1].value : null;
    const lastYear = filterYearsOptions ? filterYearsOptions[0].value : null;

    setEvolutiveBeginYear(firstYear);
    setEvolutiveEndYear(lastYear);

    // Gera colunas da tabela evolutiva conforme anos retornados da API
    let yearStringArr = [];
    let evolutiveColumnsArr = [];

    if (
      filterYearsOptions !== null && filterYearsOptions !== undefined
      && filterYearsOptions.length > 0
    ) {
      // Pega titulo das colunas
      filterYearsOptions.map((year) => {
        const newYearTitle = year.label.toString();
        yearStringArr = [...yearStringArr, newYearTitle];
        return yearStringArr;
      });

      // Organiza em ordem decrescente crescente os anos
      const filterYearsOptionsAscending = yearStringArr.sort(
        (a, b) => parseFloat(b) - parseFloat(a),
      );

      // Para cada coluna, pegar um dado diferente conforme o index no array de anos
      // Atualização do Evolutivo
      for (let i = 0; i < filterYearsOptionsAscending.length; i++) {
        const getYear = Number(yearStringArr[i]);
        if (getYear >= 2023) {
          let newEvolutiveColumnObj = {};
          newEvolutiveColumnObj = {
            Header: yearStringArr[i],
            accessor: `yearValues[${i}].mainValue`,
            Cell: cellinfo => emptyNumberCell(cellinfo.value, 1, currentUserLanguage),
          };

          // Dependendo do tipo de grupo do paciente, a quantidade de casas decimais é alterada
          if (filterIndicatorSelected === 1) {
            newEvolutiveColumnObj.Cell = cellinfo => emptyNumberCell(
              cellinfo.value, 0, currentUserLanguage,
            );
          }

          // Dependendo do tipo de grupo do paciente, o tipo de célula da tabela é alterado
          if (filterIndicatorSelected === 3) {
            newEvolutiveColumnObj.Cell = cellInfo => uniquePercentValueCell(
              cellInfo.row.original.yearValues[i].secondaryValue, 2, currentUserLanguage,
            );
          }

          if (filterIndicatorSelected === 5) {
            newEvolutiveColumnObj.Cell = cellInfo => uniquePercentValueCell(
              cellInfo.row.original.yearValues[i].secondaryValue, 2, currentUserLanguage,
            );
          }

          if (filterIndicatorSelected === 6) {
            newEvolutiveColumnObj.Cell = cellInfo => emptyNumberCell(
              cellInfo.row.original.yearValues[i].mainValue, 0, currentUserLanguage,
            );
          }

          if (filterIndicatorSelected === 10) {
            newEvolutiveColumnObj.Cell = cellInfo => uniquePercentValueCell(
              cellInfo.row.original.yearValues[i].secondaryValue, 2, currentUserLanguage,
            );
          }

          if (filterIndicatorSelected === 11) {
            newEvolutiveColumnObj.Cell = cellInfo => uniquePercentValueCell(
              cellInfo.row.original.yearValues[i].secondaryValue, 2, currentUserLanguage,
            );
          }

          evolutiveColumnsArr = [...evolutiveColumnsArr, newEvolutiveColumnObj];
        } else {
          let newEvolutiveColumnObj = {};
          newEvolutiveColumnObj = {
            Header: yearStringArr[i],
            accessor: `yearValues[${i}].mainValue`,
            Cell: cellinfo => emptyNumberCell(cellinfo.value, 1, currentUserLanguage),
          };

          // Dependendo do tipo de grupo do paciente, a quantidade de casas decimais é alterada
          if (filterIndicatorSelected === 1) {
            newEvolutiveColumnObj.Cell = cellinfo => emptyNumberCell(
              cellinfo.value, 0, currentUserLanguage,
            );
          }

          // Dependendo do tipo de grupo do paciente, o tipo de célula da tabela é alterado
          if (
            filterIndicatorSelected === 5
            || filterIndicatorSelected === 6 || filterIndicatorSelected === 10
            || filterIndicatorSelected === 11
          ) {
            newEvolutiveColumnObj.Cell = cellInfo => doubleIndicatorsCell(
              cellInfo.row.original.yearValues[i]
                ? cellInfo.row.original.yearValues[i].secondaryValue : null, 2, true,
              cellInfo.row.original.yearValues[i]
                ? cellInfo.row.original.yearValues[i].mainValue : null, 0, false,
              currentUserLanguage,
            );
          }

          if (filterIndicatorSelected === 3) {
            newEvolutiveColumnObj.Cell = cellInfo => uniquePercentValueCell(
              cellInfo.row.original.yearValues[i].secondaryValue, 1, currentUserLanguage,
            );
          }
          evolutiveColumnsArr = [...evolutiveColumnsArr, newEvolutiveColumnObj];
        }
        // Atualiza o estado global das colunas de anos
        setEvolutiveTableYearColumns(evolutiveColumnsArr);
      }
    }
  }, [filterYearsOptions, filterIndicatorSelected]);

  useEffect(() => {
    const currentLanguageVisibleOption = languageSelectRef.current.getValue();
    const currentLanguageVisibleLabel = currentLanguageVisibleOption
      && currentLanguageVisibleOption[0]
      ? currentLanguageVisibleOption[0].label : null;
    const currentLanguageOptionTranslation = languageDefaultOption
      ? languageDefaultOption.label : null;

    if (currentLanguageVisibleLabel !== currentLanguageOptionTranslation) {
      changeSelectValue(languageSelectRef, languageDefaultOption);
    }

    // Atualiza valor selecionado no select de grupo de paciente do filtro
    const currentPatientsGroupsVisibleOption = patientsGroupsSelectRef.current.getValue();
    const currentPatientsGroupsVisibleValue = currentPatientsGroupsVisibleOption
      && currentPatientsGroupsVisibleOption[0]
      ? currentPatientsGroupsVisibleOption[0].label : null;
    const currentPatientsGroupsOptionTranslation = filterPatientsGroupsDefaultOptionObj
      ? filterPatientsGroupsDefaultOptionObj.label : null;

    if (currentPatientsGroupsVisibleValue !== currentPatientsGroupsOptionTranslation) {
      changeSelectValue(patientsGroupsSelectRef, filterPatientsGroupsDefaultOptionObj);
    }

    // Atualiza valor selecionado no select de ano no filtro local
    const currentBrowsableFilterVisibleOption = browsableFilterSelectRef.current.getValue();
    const currentBrowsableFilterVisibleValue = currentBrowsableFilterVisibleOption
      && currentBrowsableFilterVisibleOption[0]
      ? currentBrowsableFilterVisibleOption[0].label : null;
    const currentYearsOptionTranslation = filterYearsDefaultOptionObj
      ? filterYearsDefaultOptionObj.label : null;
    const currentIndicatorsOptionTranslation = filterIndicatorsDefaultOptionObj
      ? filterIndicatorsDefaultOptionObj.label : null;

    if (filterReportTypeSelected === 0
      && currentBrowsableFilterVisibleValue !== currentYearsOptionTranslation) {
      changeSelectValue(browsableFilterSelectRef, filterYearsDefaultOptionObj);
    }

    // Atualiza valor selecionado no select de indicador no filtro local
    if (filterReportTypeSelected === 1
      && currentBrowsableFilterVisibleValue !== currentIndicatorsOptionTranslation) {
      changeSelectValue(browsableFilterSelectRef, filterIndicatorsDefaultOptionObj);
    }
  }, [currentUserLanguage, Portuguese]); // filterPatientsGroupsDefaultOptionObj

  useEffect(() => {
    // Reseta filtro local para o valor padrão ao mudar de idioma
    if (filterPatientsGroupsDefaultOptionObj !== null && filterYearsDefaultOptionObj !== null
      && filterIndicatorsDefaultOptionObj !== null) {
      updateFiltersAppliedStorage();
    }
  }, [currentUserLanguage]);

  // Atualiza estados das colunas das tabelas
  useMemo(() => {
    setConsolidatedIndicatorsTableColumns([
      {
        Header: Registry,
        accessor: 'regionName',
        id: 'regionName',
        Cell: cellinfo => recordsHoverableCell(
          cellinfo, setRecordInformationsTitle, setModalRecordInformationsLogo,
          setModalRecordInformationsCountryName, setModalRecordInformationsHospitalsCount,
          setModalRecordInformationsUnitsCount, setModalRecordInformationsSourceSite,
          setModalRecordInformationsDescription,
        ),
        sticky: 'left', // Propriedade que deixa a coluna fixa
        width: 160,
      },
      {
        Header: Country,
        accessor: 'countryName',
        id: 'countryName',
        Cell: emptyStringCell,
        sticky: 'left',
        width: 160,
      },
      {
        Header: Admissions,
        accessor: 'admissionsCount',
        id: 'admissionsCount',
        Cell: cellinfo => emptyNumberCell(cellinfo.value, 0, currentUserLanguage),
        width: 120,
      },
      {
        Header: Age,
        accessor: 'ageMeanInYears',
        id: 'ageMeanInYears',
        Cell: cellinfo => emptyNumberCell(cellinfo.value, 1, currentUserLanguage),
        width: 80,
      },
      {
        Header: FortyEightreadmissions,
        accessor: 'fortyEightReadmissionsPercent',
        id: 'fortyEightReadmissionsPercent',
        Cell: cellInfo => uniquePercentValueCell(
          cellInfo.row.original.fortyEightReadmissionsPercent, 2, currentUserLanguage,
        ),
        width: 160,
      },
      {
        Header: SOFA,
        accessor: 'sofaMeanPoints',
        id: 'sofaMeanPoints',
        Cell: cellinfo => emptyNumberCell(cellinfo.value, 1, currentUserLanguage),
        width: 112,
      },
      {
        Header: RenalReplacementTherapy,
        accessor: 'renalReplacementTherapyPercent',
        id: 'renalReplacementTherapyPercent',
        Cell: (cellInfo) => {
          if (cellInfo.row.original.year <= 2022) {
            return doubleIndicatorsCell(
              cellInfo.row.original.renalReplacementTherapyPercent, 2, true,
              cellInfo.row.original.renalReplacementTherapyCount, 0, false,
              currentUserLanguage,
            );
          }
          return uniquePercentValueCell(
            cellInfo.row.original.renalReplacementTherapyPercent, 2, currentUserLanguage,
          );
        },
        width: 200,
      },
      {
        Header: MechanicalVentilation,
        accessor: 'mechanicalVentilationCount',
        id: 'mechanicalVentilationCount',
        Cell: (cellInfo) => {
          if (cellInfo.row.original.year <= 2022) {
            return doubleIndicatorsCell(
              cellInfo.row.original.mechanicalVentilationPercent, 2, true,
              cellInfo.row.original.mechanicalVentilationCount, 0, false,
              currentUserLanguage,
            );
          }
          return emptyNumberCell(cellInfo.value, 0, currentUserLanguage);
        },
        width: 176,
      },
      {
        Header: MechanicalVentilationDurationDays,
        accessor: 'mechanicalVentilationDurationMeanInDays',
        id: 'mechanicalVentilationDurationMeanInDays',
        Cell: cellinfo => emptyNumberCell(cellinfo.value, 1, currentUserLanguage),
        width: 216,
        helpText: Median,
      },
      {
        Header: ICULOSDays,
        accessor: 'lengthOfStayICUUnitMeanInDays',
        id: 'lengthOfStayICUUnitMeanInDays',
        Cell: cellinfo => emptyNumberCell(cellinfo.value, 1, currentUserLanguage),
        width: 144,
        helpText: Median,
      },
      {
        Header: HospitalLOSDays,
        accessor: 'lengthOfStayICUHospitalMeanInDays',
        id: 'lengthOfStayICUHospitalMeanInDays',
        Cell: cellinfo => emptyNumberCell(cellinfo.value, 1, currentUserLanguage),
        width: 144,
        helpText: Median,
      },
      {
        Header: ICUMortality,
        accessor: 'mortalityICUUnitMeanInDays',
        id: 'mortalityICUUnitMeanInDays',
        Cell: (cellInfo) => {
          if (cellInfo.row.original.year <= 2022) {
            return doubleIndicatorsCell(
              cellInfo.row.original.mortalityICUUnitMeanInDays, 2, true,
              cellInfo.row.original.mortalityICUUnitCount, 0, false,
              currentUserLanguage,
            );
          }
          return uniquePercentValueCell(
            cellInfo.row.original.mortalityICUUnitMeanInDays, 2, currentUserLanguage,
          );
        },
        width: 160,
      },
      {
        Header: HospitalMortality,
        accessor: 'mortalityICUHospitalMeanInDays',
        id: 'mortalityICUHospitalMeanInDays',
        Cell: (cellInfo) => {
          if (cellInfo.row.original.year <= 2022) {
            return doubleIndicatorsCell(
              cellInfo.row.original.mortalityICUHospitalMeanInDays, 2, true,
              cellInfo.row.original.mortalityICUHospitalCount, 0, false,
              currentUserLanguage,
            );
          }
          return uniquePercentValueCell(
            cellInfo.row.original.mortalityICUHospitalMeanInDays, 2, currentUserLanguage,
          );
        },
        width: 160,
      },
    ]);

    setEvolutiveIndicatorsTableColumns([
      {
        Header: Registry,
        accessor: 'regionName',
        id: 'regionName',
        Cell: cellinfo => recordsHoverableCell(
          cellinfo, setRecordInformationsTitle, setModalRecordInformationsLogo,
          setModalRecordInformationsCountryName, setModalRecordInformationsHospitalsCount,
          setModalRecordInformationsUnitsCount, setModalRecordInformationsSourceSite,
          setModalRecordInformationsDescription,
        ),
        sticky: 'left',
        width: 160,
      },
      {
        Header: Country,
        accessor: 'countryName',
        id: 'countryName',
        Cell: emptyStringCell,
        sticky: 'left',
        width: 160,
      },
      {
        Header: `${Trending} (${evolutiveBeginYear} ${To} ${evolutiveEndYear})`,
        accessor: '',
        id: '',
        Cell: cellinfo => tendencyChartCell(cellinfo, currentUserLanguage),
        sticky: 'left',
        disableSortBy: true, // propriedade para desabilitar a ordenação dos dados da coluna
        width: 240,
      },
      ...evolutiveTableYearColumns,
    ]);
  }, [evolutiveBeginYear, evolutiveEndYear, evolutiveTableYearColumns]);

  // Alteração Totalizadores
  if (lastFiltersApplied.year >= 2023) {
    return (
      <>
        <EpToaster />

        <EpLoadingPage isVisible={isLoadingPage} />

        <EpRecordInformationsModal
          modalTitle={modalRecordInformationsTitle}
          modalRecordLogo={modalRecordInformationsLogo}
          modalRecordCountryName={modalRecordInformationsCountryName}
          modalRecordHospitalsCount={modalRecordInformationsHospitalsCount}
          modalRecordUnitsCount={modalRecordInformationsUnitsCount}
          modalRecordSourceSite={modalRecordInformationsSourceSite}
          modalRecordDescription={modalRecordInformationsDescription}
        />

        <div id="benchmarking-page">
          <EpGlobalNavigation
            handleChangeLanguage={handleChangeLanguage}
            languageSelectRef={languageSelectRef}
            languageOptions={languageOptions}
            languageDefaultOption={languageDefaultOption}
          />

          <EpFilter
            selectPatientsGroupsOptions={filterPatientsGroupsOptions}
            selectPatientsGroupsDefaultValue={filterPatientsGroupsDefaultOptionObj}
            lastFiltersApplied={lastFiltersApplied}
            setConsolidatedTotalizerIndicatorsDataFunc={setConsolidatedTotalizerIndicatorsData}
            setConsolidatedIndicatorsDataFunc={setConsolidatedIndicatorsData}
            setEvolutiveIndicatorsDataFunc={setEvolutiveIndicatorsData}
            setIsLoadingPageFunc={setIsLoadingPage}
            patientsGroupsSelectRef={patientsGroupsSelectRef}
            browsableFilterSelectRef={browsableFilterSelectRef}
            filterIndicatorsDefaultOptionObj={filterIndicatorsDefaultOptionObj}
            filterYearsDefaultOptionObj={filterYearsDefaultOptionObj}
            setFilterYearsDefaultOptionObjFunc={setFilterYearsDefaultOptionObj}
          />

          <div className="container--custom">
            {filterReportTypeSelected === 0 && (
              <>
                {/* Filtro Navegável dos Dados Consolidados */}
                <EpBrowsableFilter
                  selectOptions={filterYearsOptions}
                  inputId="browsable-filter__select--years"
                  labelText={`${Year}:`}
                  inputName="year"
                  selectDefaultValue={filterYearsDefaultOptionObj}
                  selectIsSearchable={false}
                  horizontalAlign="center"
                  lastFiltersApplied={lastFiltersApplied}
                  setConsolidatedTotalizerIndicatorsDataFunc={
                    setConsolidatedTotalizerIndicatorsData
                  }
                  setConsolidatedIndicatorsDataFunc={setConsolidatedIndicatorsData}
                  setEvolutiveIndicatorsDataFunc={setEvolutiveIndicatorsData}
                  setIsLoadingPageFunc={setIsLoadingPage}
                  browsableFilterSelectRef={browsableFilterSelectRef}
                />

                {/* Totalizadores dos Dados Consolidados */}
                <div className="benchmarking-page__indicators-totalizers benchmarking-page__indicators-totalizers--consolidated">
                  <div className="row">
                    <div className="col">
                      <EpCardIndicator
                        indicatorType="single-value"
                        indicatorTitle={Admissions}
                        indicatorValuePrimary={[consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.admissionsCount : null]}
                        indicatorValuePrimaryDigits={0}
                        indicatorName="admissionsCount"
                      />
                    </div>
                    <div className="col">
                      <EpCardIndicator
                        indicatorType="single-value"
                        indicatorTitle={Age}
                        indicatorValuePrimary={consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.ageMeanInYears : null}
                        indicatorMeasureUnitPrimary={Years}
                        indicatorValuePrimaryDigits={1}
                        indicatorName="ageMeanInYears"
                      />
                    </div>
                    <div className="col">
                      <EpCardIndicator
                        indicatorType="single-values"
                        indicatorTitle={FortyEightreadmissions}
                        indicatorPercentage={[
                          consolidatedTotalizerIndicatorsData
                            ? consolidatedTotalizerIndicatorsData.fortyEightReadmissionsPercent
                            : null,
                        ]}
                        indicatorPercentageDigits={2}
                        indicatorName="fortyEightReadmissions"
                      />
                    </div>
                    <div className="col">
                      <EpCardIndicator
                        indicatorType="single-value"
                        indicatorTitle={SOFA}
                        indicatorValuePrimary={consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.sofaMeanPoints : null}
                        indicatorMeasureUnitPrimary={Points}
                        indicatorValuePrimaryDigits={1}
                        indicatorName="sofaMeanPoints"
                      />
                    </div>
                    <div className="col">
                      <EpCardIndicator
                        indicatorType="single-values"
                        indicatorTitle={RenalReplacementTherapy}
                        indicatorPercentage={[
                          consolidatedTotalizerIndicatorsData
                            ? consolidatedTotalizerIndicatorsData.renalReplacementTherapyPercent
                            : null,
                        ]}
                        indicatorPercentageDigits={2}
                        indicatorName="renalReplacementTherapy"
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col">
                      <EpCardIndicator
                        indicatorType="double-indicators"
                        indicatorTitle={MechanicalVentilation}
                        indicatorSubtitle={[Patients, Mean]}
                        indicatorValuePrimary={[
                          consolidatedTotalizerIndicatorsData
                            ? consolidatedTotalizerIndicatorsData.mechanicalVentilationCount
                            : null,
                          consolidatedTotalizerIndicatorsData
                            ? consolidatedTotalizerIndicatorsData
                              .mechanicalVentilationDurationMeanInDays
                            : null,
                        ]}
                        indicatorMeasureUnitPrimary={[null, Days]}
                        indicatorValuePrimaryDigits={[null, 1]}
                        indicatorValueSecondaryDigits={[0, null]}
                        indicatorName="mechanicalVentilation"
                      />
                    </div>
                    <div className="col">
                      <EpCardIndicator
                        indicatorType="double-indicators"
                        indicatorTitle={LOS}
                        indicatorSubtitle={[ICU, Hospital]}
                        indicatorValuePrimary={[
                          consolidatedTotalizerIndicatorsData
                            ? consolidatedTotalizerIndicatorsData.lengthOfStayICUUnitMeanInDays
                            : null,
                          consolidatedTotalizerIndicatorsData
                            ? consolidatedTotalizerIndicatorsData.lengthOfStayICUHospitalMeanInDays
                            : null,
                        ]}
                        indicatorMeasureUnitPrimary={[Days, Days]}
                        indicatorValuePrimaryDigits={[1, 1]}
                        indicatorName="lengthOfStayICUHospitalMean"
                      />
                    </div>

                    <div className="col">
                      <EpCardIndicator
                        indicatorType="double-indicators"
                        indicatorTitle={Mortality}
                        indicatorSubtitle={[ICU, Hospital]}
                        indicatorPercentage={[
                          consolidatedTotalizerIndicatorsData
                            ? consolidatedTotalizerIndicatorsData.mortalityICUUnitMeanInDays : null,
                          consolidatedTotalizerIndicatorsData
                            ? consolidatedTotalizerIndicatorsData.mortalityICUHospitalMeanInDays
                            : null,
                        ]}
                        indicatorValueSecondary={[
                          null,
                          null,
                        ]}
                        indicatorValueSecondaryDigits={[0, 0]}
                        indicatorPercentageDigits={[2, 2]}
                        indicatorName="mortalityICUHospital"
                      />
                    </div>
                  </div>
                </div>

                {/* Tabela dos Dados Consolidados */}
                <div className="benchmarking-page__indicators benchmarking-page__indicators--consolidated">
                  {consolidatedTotalizerIndicatorsData
                    && (
                      <EpTable
                        tableData={consolidatedIndicatorsData}
                        columns={consolidatedIndicatorsTableColumns}
                        getCellProps={cellInfo => coloredConsolidatedIndicatorCell(
                          cellInfo, consolidatedIndicatorsData,
                        )}
                      />
                    )}
                </div>
              </>
            )}

            {filterReportTypeSelected === 1 && (
              <>
                {/* Filtro Navegável dos Dados Evolutivos */}
                <EpBrowsableFilter
                  selectOptions={filterIndicatorsOptions}
                  inputId="browsable-filter__select--indicator"
                  labelText={`${Indicator}:`}
                  inputName="indicator"
                  selectDefaultValue={filterIndicatorsDefaultOptionObj}
                  selectIsSearchable={false}
                  horizontalAlign="space-between"
                  lastFiltersApplied={lastFiltersApplied}
                  setConsolidatedTotalizerIndicatorsDataFunc={
                    setConsolidatedTotalizerIndicatorsData
                  }
                  setConsolidatedIndicatorsDataFunc={setConsolidatedIndicatorsData}
                  setEvolutiveIndicatorsDataFunc={setEvolutiveIndicatorsData}
                  setIsLoadingPageFunc={setIsLoadingPage}
                  browsableFilterSelectRef={browsableFilterSelectRef}
                />

                {/* Tabela dos Dados Evolutivos */}
                <div className="benchmarking-page__indicators benchmarking-page__indicators--evolutive">
                  <EpTable
                    tableData={evolutiveIndicatorsData}
                    columns={evolutiveIndicatorsTableColumns}
                    getCellProps={cellInfo => coloredEvolutiveIndicatorCell(
                      cellInfo, evolutiveIndicatorsData, filterYearsOptions,
                    )}
                  />
                </div>
              </>
            )}
            <EpGlobalFooter
              handleChangeLanguage={handleChangeLanguage}
              languageOptions={languageOptions}
              languageDefaultOption={languageDefaultOption}
            />
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <EpToaster />

      <EpLoadingPage isVisible={isLoadingPage} />

      <EpRecordInformationsModal
        modalTitle={modalRecordInformationsTitle}
        modalRecordLogo={modalRecordInformationsLogo}
        modalRecordCountryName={modalRecordInformationsCountryName}
        modalRecordHospitalsCount={modalRecordInformationsHospitalsCount}
        modalRecordUnitsCount={modalRecordInformationsUnitsCount}
        modalRecordSourceSite={modalRecordInformationsSourceSite}
        modalRecordDescription={modalRecordInformationsDescription}
      />

      <div id="benchmarking-page">
        <EpGlobalNavigation
          handleChangeLanguage={handleChangeLanguage}
          languageSelectRef={languageSelectRef}
          languageOptions={languageOptions}
          languageDefaultOption={languageDefaultOption}
        />

        <EpFilter
          selectPatientsGroupsOptions={filterPatientsGroupsOptions}
          selectPatientsGroupsDefaultValue={filterPatientsGroupsDefaultOptionObj}
          lastFiltersApplied={lastFiltersApplied}
          setConsolidatedTotalizerIndicatorsDataFunc={setConsolidatedTotalizerIndicatorsData}
          setConsolidatedIndicatorsDataFunc={setConsolidatedIndicatorsData}
          setEvolutiveIndicatorsDataFunc={setEvolutiveIndicatorsData}
          setIsLoadingPageFunc={setIsLoadingPage}
          patientsGroupsSelectRef={patientsGroupsSelectRef}
          browsableFilterSelectRef={browsableFilterSelectRef}
          filterIndicatorsDefaultOptionObj={filterIndicatorsDefaultOptionObj}
          filterYearsDefaultOptionObj={filterYearsDefaultOptionObj}
          setFilterYearsDefaultOptionObjFunc={setFilterYearsDefaultOptionObj}
        />

        <div className="container--custom">
          {filterReportTypeSelected === 0 && (
            <>
              {/* Filtro Navegável dos Dados Consolidados */}
              <EpBrowsableFilter
                selectOptions={filterYearsOptions}
                inputId="browsable-filter__select--years"
                labelText={`${Year}:`}
                inputName="year"
                selectDefaultValue={filterYearsDefaultOptionObj}
                selectIsSearchable={false}
                horizontalAlign="center"
                lastFiltersApplied={lastFiltersApplied}
                setConsolidatedTotalizerIndicatorsDataFunc={setConsolidatedTotalizerIndicatorsData}
                setConsolidatedIndicatorsDataFunc={setConsolidatedIndicatorsData}
                setEvolutiveIndicatorsDataFunc={setEvolutiveIndicatorsData}
                setIsLoadingPageFunc={setIsLoadingPage}
                browsableFilterSelectRef={browsableFilterSelectRef}
              />

              {/* Totalizadores dos Dados Consolidados */}
              <div className="benchmarking-page__indicators-totalizers benchmarking-page__indicators-totalizers--consolidated">
                <div className="row">
                  <div className="col">
                    <EpCardIndicator
                      indicatorType="single-value"
                      indicatorTitle={Admissions}
                      indicatorValuePrimary={[consolidatedTotalizerIndicatorsData
                        ? consolidatedTotalizerIndicatorsData.admissionsCount : null]}
                      indicatorValuePrimaryDigits={0}
                      indicatorName="admissionsCount"
                    />
                  </div>
                  <div className="col">
                    <EpCardIndicator
                      indicatorType="single-value"
                      indicatorTitle={Age}
                      indicatorValuePrimary={consolidatedTotalizerIndicatorsData
                        ? consolidatedTotalizerIndicatorsData.ageMeanInYears : null}
                      indicatorMeasureUnitPrimary={Years}
                      indicatorValuePrimaryDigits={1}
                      indicatorName="ageMeanInYears"
                    />
                  </div>
                  <div className="col">
                    <EpCardIndicator
                      indicatorType="single-values"
                      indicatorTitle={FortyEightreadmissions}
                      indicatorPercentage={[
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.fortyEightReadmissionsPercent
                          : null,
                      ]}
                      indicatorPercentageDigits={2}
                      indicatorName="fortyEightReadmissions"
                    />
                  </div>
                  <div className="col">
                    <EpCardIndicator
                      indicatorType="single-value"
                      indicatorTitle={SOFA}
                      indicatorValuePrimary={consolidatedTotalizerIndicatorsData
                        ? consolidatedTotalizerIndicatorsData.sofaMeanPoints : null}
                      indicatorMeasureUnitPrimary={Points}
                      indicatorValuePrimaryDigits={1}
                      indicatorName="sofaMeanPoints"
                    />
                  </div>
                  <div className="col">
                    <EpCardIndicator
                      indicatorType="double-values"
                      indicatorTitle={RenalReplacementTherapy}
                      indicatorPercentage={[
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.renalReplacementTherapyPercent
                          : null,
                      ]}
                      indicatorValueSecondary={[
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.renalReplacementTherapyCount : null,
                      ]}
                      indicatorValueSecondaryDigits={0}
                      indicatorPercentageDigits={2}
                      indicatorName="renalReplacementTherapy"
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col">
                    <EpCardIndicator
                      indicatorType="double-indicators"
                      indicatorTitle={MechanicalVentilation}
                      indicatorSubtitle={[Patients, Mean]}
                      indicatorValuePrimary={[
                        null,
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData
                            .mechanicalVentilationDurationMeanInDays
                          : null,
                      ]}
                      indicatorPercentage={[
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.mechanicalVentilationPercent : null,
                        null,
                      ]}
                      indicatorValueSecondary={[
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.mechanicalVentilationCount : null,
                        null,
                      ]}
                      indicatorMeasureUnitPrimary={[null, Days]}
                      indicatorValuePrimaryDigits={[null, 1]}
                      indicatorValueSecondaryDigits={[0, null]}
                      indicatorPercentageDigits={[2, null]}
                      indicatorName="mechanicalVentilation"
                    />
                  </div>
                  <div className="col">
                    <EpCardIndicator
                      indicatorType="double-indicators"
                      indicatorTitle={LOS}
                      indicatorSubtitle={[ICU, Hospital]}
                      indicatorValuePrimary={[
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.lengthOfStayICUUnitMeanInDays
                          : null,
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.lengthOfStayICUHospitalMeanInDays
                          : null,
                      ]}
                      indicatorMeasureUnitPrimary={[Days, Days]}
                      indicatorValuePrimaryDigits={[1, 1]}
                      indicatorName="lengthOfStayICUHospitalMean"
                    />
                  </div>

                  <div className="col">
                    <EpCardIndicator
                      indicatorType="double-indicators"
                      indicatorTitle={Mortality}
                      indicatorSubtitle={[ICU, Hospital]}
                      indicatorPercentage={[
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.mortalityICUUnitMeanInDays : null,
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.mortalityICUHospitalMeanInDays
                          : null,
                      ]}
                      indicatorValueSecondary={[
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.mortalityICUUnitCount : null,
                        consolidatedTotalizerIndicatorsData
                          ? consolidatedTotalizerIndicatorsData.mortalityICUHospitalCount : null,
                      ]}
                      indicatorValueSecondaryDigits={[0, 0]}
                      indicatorPercentageDigits={[2, 2]}
                      indicatorName="mortalityICUHospital"
                    />
                  </div>
                </div>
              </div>

              {/* Tabela dos Dados Consolidados */}
              <div className="benchmarking-page__indicators benchmarking-page__indicators--consolidated">
                {consolidatedTotalizerIndicatorsData
                  && (
                    <EpTable
                      tableData={consolidatedIndicatorsData}
                      columns={consolidatedIndicatorsTableColumns}
                      getCellProps={cellInfo => coloredConsolidatedIndicatorCell(
                        cellInfo, consolidatedIndicatorsData,
                      )}
                    />
                  )}
              </div>
            </>
          )}

          {filterReportTypeSelected === 1 && (
            <>
              {/* Filtro Navegável dos Dados Evolutivos */}
              <EpBrowsableFilter
                selectOptions={filterIndicatorsOptions}
                inputId="browsable-filter__select--indicator"
                labelText={`${Indicator}:`}
                inputName="indicator"
                selectDefaultValue={filterIndicatorsDefaultOptionObj}
                selectIsSearchable={false}
                horizontalAlign="space-between"
                lastFiltersApplied={lastFiltersApplied}
                setConsolidatedTotalizerIndicatorsDataFunc={setConsolidatedTotalizerIndicatorsData}
                setConsolidatedIndicatorsDataFunc={setConsolidatedIndicatorsData}
                setEvolutiveIndicatorsDataFunc={setEvolutiveIndicatorsData}
                setIsLoadingPageFunc={setIsLoadingPage}
                browsableFilterSelectRef={browsableFilterSelectRef}
              />

              {/* Tabela dos Dados Evolutivos */}
              <div className="benchmarking-page__indicators benchmarking-page__indicators--evolutive">
                <EpTable
                  tableData={evolutiveIndicatorsData}
                  columns={evolutiveIndicatorsTableColumns}
                  getCellProps={cellInfo => coloredEvolutiveIndicatorCell(
                    cellInfo, evolutiveIndicatorsData, filterYearsOptions,
                  )}
                />
              </div>
            </>
          )}
          <EpGlobalFooter
            handleChangeLanguage={handleChangeLanguage}
            languageOptions={languageOptions}
            languageDefaultOption={languageDefaultOption}
          />
        </div>
      </div>
    </>
  );
};

export default Benchmarking;
